import careConfig from "@careConfig";

/**
 * Patient authentication helpers (no staff auth involved).
 *
 * Two independent surfaces:
 *  - Elpako eID (Smart-ID / m. parašas) — talks to CARE
 *    (`/api/v1/auth/elpako/*`), which verifies via Elpako, upserts the
 *    patient and mints a CARE patient JWT.
 *  - QR "log in with the app" — talks to the app backend
 *    (`careConfig.appApiUrl`): opens the web-login WebSocket, the app
 *    approves the scanned session, then `/care/session` exchanges the app
 *    access token for a CARE patient JWT.
 */

export type ElpakoMethod = "smart_id" | "mobile";

export interface ElpakoStartResult {
  method: ElpakoMethod;
  session: string;
  control_code: string | null;
}

export interface ElpakoPollResult {
  status: "waiting" | "ok" | "error";
  access?: string;
  patient_id?: string;
  name?: string;
  detail?: string | null;
}

function careUrl(path: string): string {
  return `${careConfig.apiUrl}${path}`;
}

/** DRF returns 400 as a list of strings or {msg}/{loc} objects — flatten it. */
async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (Array.isArray(body)) {
      const first = body[0];
      if (typeof first === "string") return first;
      if (first?.msg) return String(first.msg);
    }
    if (body?.detail) return String(body.detail);
  } catch {
    // ignore
  }
  return "Prisijungimo klaida";
}

export async function elpakoStart(input: {
  method: ElpakoMethod;
  personalCode: string;
  phone?: string;
  country?: string;
}): Promise<ElpakoStartResult> {
  const res = await fetch(careUrl("/api/v1/auth/elpako/start/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: input.method,
      personal_code: input.personalCode,
      phone: input.phone,
      country: input.country ?? "lt",
    }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

export async function elpakoPoll(input: {
  method: ElpakoMethod;
  session: string;
  phone?: string;
}): Promise<ElpakoPollResult> {
  const res = await fetch(careUrl("/api/v1/auth/elpako/poll/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: input.method,
      session: input.session,
      phone: input.phone,
    }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

export interface AppWebLoginHandlers {
  onSession: (sessionId: string) => void;
  /** QR nuskenuotas (dar nepatvirtinta) — uždengti QR, rodyti „patvirtinkite". */
  onScanned?: () => void;
  /** App'as atšaukė po nuskenavimo — nuimti blur, leisti skenuoti vėl. */
  onCancelled?: () => void;
  onApproved: (appAccessToken: string) => void;
  onError: () => void;
}

export interface AppWebLoginHandle {
  close: () => void;
}

/** QR payload the app scanner expects (see app web-login.tsx). */
export function webLoginQrPayload(sessionId: string): string {
  return `avitra://web-login?session=${encodeURIComponent(sessionId)}`;
}

/**
 * Open the app backend web-login WebSocket. On open the server sends the
 * session id (render it as a QR); once the app approves, it pushes the app
 * access token.
 */
export function openAppWebLogin(
  handlers: AppWebLoginHandlers,
): AppWebLoginHandle {
  const base = careConfig.appApiUrl;
  const wsUrl = `${base.replace(/^http/, "ws")}/ws/web-login`;
  let socket: WebSocket | null = null;
  try {
    socket = new WebSocket(wsUrl);
  } catch {
    handlers.onError();
    return { close: () => {} };
  }
  socket.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string);
      if (msg.type === "session" && msg.sessionId) {
        handlers.onSession(msg.sessionId);
      } else if (msg.type === "scanned") {
        handlers.onScanned?.();
      } else if (msg.type === "cancelled") {
        handlers.onCancelled?.();
      } else if (msg.type === "approved" && msg.accessToken) {
        handlers.onApproved(msg.accessToken);
      }
    } catch {
      // ignore malformed frames
    }
  };
  socket.onerror = () => handlers.onError();
  return {
    close: () => {
      try {
        socket?.close();
      } catch {
        // ignore
      }
    },
  };
}

/** Exchange an app access token for a CARE patient session. */
export async function careSessionFromApp(
  appAccessToken: string,
): Promise<{ access: string; patient_id: string }> {
  const res = await fetch(`${careConfig.appApiUrl}/care/session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${appAccessToken}`,
    },
  });
  if (!res.ok) throw new Error(await readError(res));
  const body = await res.json();
  const data = body?.data ?? body;
  return { access: data.access, patient_id: data.patient_id };
}
